'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { MdDiscount } from 'react-icons/md';
import { cn } from '@/lib/styleUtils';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonVariant } from '@/components/ui/button/Button.types';
import { EmptyState } from '@/components/ui/empty-state';
import { RateSelectionTableProps, RateActionState } from './ChangeDiscount.types';

/**
 * Rate selection list for the Change Discount Program modal.
 * Card-based layout matching Figma: discount icon + program name + Select button.
 */
export function RateSelectionTable({ rates, onSelectRate, loading }: RateSelectionTableProps) {
  const t = useTranslations('ParkingValidation');

  const getActionLabel = (state: RateActionState, expiryDate?: string): string => {
    switch (state) {
      case 'currently_applied':
        return t('changeDiscountCurrentlyApplied');
      case 'selectable':
        return t('changeDiscountSelect');
      case 'not_applicable':
        return t('changeDiscountNotApplicable');
      case 'usage_exceeded':
        return t('changeDiscountUsageExceeded');
      case 'expired':
        return t('changeDiscountExpired', { date: expiryDate || '' });
    }
  };

  const isDisabled = (state: RateActionState): boolean => state !== 'selectable';

  return (
    <div className="tw:flex tw:flex-col tw:gap-small" data-cy="change-discount-rate-list">
      {rates.map((rate, index) => (
        <div
          key={`${rate.specialRateId}-${index}`}
          className="tw:flex tw:items-center tw:gap-base tw:px-base tw:py-small tw:border tw:border-solid tw:border-gray-light tw:rounded-8"
          data-cy="change-discount-rate-row"
        >
          <div className="tw:flex tw:flex-1 tw:items-center tw:gap-small tw:min-w-0">
            <div className="tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:bg-gray-lighter tw:shrink-0">
              <MdDiscount size={16} className="tw:text-gray-darker" aria-hidden="true" />
            </div>
            <span
              className={cn(
                'tw:text-16 tw:text-font tw:truncate tw:min-w-0',
                isDisabled(rate.actionState) &&
                  rate.actionState !== 'currently_applied' &&
                  'tw:text-gray-dark'
              )}
              data-cy="change-discount-rate-name"
            >
              {rate.specialRateName}
            </span>
          </div>
          <Button
            variant={ButtonVariant.secondary}
            size={ButtonSize.small}
            disabled={isDisabled(rate.actionState) || loading}
            onClick={() => onSelectRate(rate.specialRateId)}
            className="tw:shrink-0"
            data-cy="change-discount-select-button"
          >
            {getActionLabel(rate.actionState, rate.expiryDate)}
          </Button>
        </div>
      ))}

      {rates.length === 0 && (
        <EmptyState
          iconContainer={{ icon: <MdDiscount size={48} aria-hidden="true" /> }}
          title={t('changeDiscountNoProgramsTitle')}
          subtitle={t('changeDiscountNoProgramsSubtitle')}
          data-cy="change-discount-empty"
        />
      )}
    </div>
  );
}
